import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CompetencyArea, CompetencyWithEvaluation, GrowthWheel } from '../types/GrowthWheel'

export interface GrowthWheelState {
  growthWheelData: GrowthWheel | null
  userCompetencyAreasData: CompetencyArea[]
  isLoadingGrowthWheel: boolean
  currentCompetencyArea: CompetencyArea | null
  currentCompetency: CompetencyWithEvaluation | null
  focusCompetenciesLimit: boolean,
  growthWheelCompleted: boolean
  currentAreasOfFocus: CompetencyWithEvaluation[]
}

const initialState: GrowthWheelState = {
  growthWheelData: null,
  userCompetencyAreasData: [],
  isLoadingGrowthWheel: false,
  currentCompetencyArea: null,
  currentCompetency: null,
  focusCompetenciesLimit: false,
  growthWheelCompleted: false,
  currentAreasOfFocus: [],
}

const growthWheelSlice = createSlice({
  name: 'growthWheel',
  initialState,
  reducers: {
    setGrowthWheelData: (state, action: PayloadAction<GrowthWheel | null>) => {
      state.growthWheelData = action.payload
    },
    setUserCompetencyAreasData: (state, action: PayloadAction<CompetencyArea[]>) => {
      state.userCompetencyAreasData = action.payload
    },
    setLoadingGrowthWheel: (state, action: PayloadAction<boolean>) => {
      state.isLoadingGrowthWheel = action.payload
    },
    setCurrentCompetencyArea: (state, action: PayloadAction<CompetencyArea | null>) => {
      state.currentCompetencyArea = action.payload
    },
    setCurrentCompetency: (state, action: PayloadAction<CompetencyWithEvaluation | null>) => {
      state.currentCompetency = action.payload
    },
    setCurrentAreasOfFocus: (state) => {
      const allCompetencies = getAllCompetencies(state)
      const currentAreasOfFocus = allCompetencies.filter((competency) => competency.evaluation?.focus)
      const currentAreasOfFocusWithAreaNames = currentAreasOfFocus.map((competency) => {
        const competencyArea = state.userCompetencyAreasData.find((area) => area.competencies?.some((areaCompetency) => areaCompetency.id === competency.id))
        return {
          ...competency,
          areaName: competencyArea?.name,
          areaId: competencyArea?.id,
        }
      })
      state.currentAreasOfFocus = currentAreasOfFocusWithAreaNames
    },
    evaluateFocusCompetenciesLimit: (state) => {
      const allCompetencies = getAllCompetencies(state)
      const focusCompetencies = allCompetencies.filter((competency) => competency.evaluation?.focus)
      if (focusCompetencies.length >= 3) {
        state.focusCompetenciesLimit = true
      } else {
        state.focusCompetenciesLimit = false
      }
    },
    evaluateGrowthWheelCompletion: (state) => {
      const allCompetencies = getAllCompetencies(state)
      const completedCompetencies = allCompetencies.filter((competency) => competency.evaluation?.selfLevel !== null && competency.evaluation?.leaderLevel !== null)
      if (completedCompetencies.length === allCompetencies.length) {
        state.growthWheelCompleted = true
      } else {
        state.growthWheelCompleted = false
      }
    },
    resetGrowthWheelState: () => initialState,
  },
})

const getAllCompetencies = (state: GrowthWheelState) => {
  return state.userCompetencyAreasData.flatMap((area) => area.competencies ?? [])
}

export const {
  setGrowthWheelData,
  setUserCompetencyAreasData,
  setLoadingGrowthWheel,
  setCurrentCompetencyArea,
  setCurrentCompetency,
  resetGrowthWheelState,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setCurrentAreasOfFocus,
} = growthWheelSlice.actions

export default growthWheelSlice.reducer
